import type { MouseEvent, ReactNode } from 'react';
import type { IHeader, ISwimlaneActions } from '@/common/components/ui/swimlane';
import {
  ColumnHeader as SwimlaneColumnHeader,
  RowHeader as SwimlaneRowHeader,
} from '@/common/components/ui/swimlane';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { SwimlaneCustomerChip } from '@/features/items/components/custom-field-value/CustomerFieldDisplay';
import {
  DEFAULT_FALLBACK_COLOR,
  getContrastTextColor,
} from '@/features/visualizations/utils/colors';

/**
 * Extract headerColor from header props at runtime.
 *
 * The swimlane component's IHeader type only includes { id, label, isDateHeader },
 * but ISwimlaneHeaderProps (passed as columnHeaders/rowHeaders) includes headerColor.
 * Since the swimlane passes IHeader to slot components, we need runtime checking
 * to safely access the headerColor property that exists on the actual data.
 */
export const resolveHeaderColor = (header: IHeader): string | undefined => {
  const headerWithColor = header as IHeader & { headerColor?: string };
  return headerWithColor.headerColor;
};

/**
 * Render either the plain text label or a row of `SwimlaneCustomerChip`s when
 * the header carries `MULTI_CUSTOMER` displayValues. Mirrors the in-app
 * `renderHeaderLabel` used by the editable swimlane (Swimlane.tsx) so shared
 * links and PDF exports stay visually identical to the authoring experience.
 */
function renderReadOnlyHeaderLabel(
  props: IHeader,
  includeCustomerDisplayValues: boolean,
): ReactNode {
  const displayValues = Array.isArray(props.displayValues) ? props.displayValues : [];
  const customerDisplayValues = includeCustomerDisplayValues
    ? displayValues.filter((displayValue) => displayValue.type === 'customer')
    : [];

  if (customerDisplayValues.length === 0) {
    return props.label || '';
  }

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2">
      {customerDisplayValues.map((customer) => (
        <SwimlaneCustomerChip key={customer.id} customer={customer} />
      ))}
    </span>
  );
}

export function ReadOnlyColumnHeader(props: IHeader) {
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');
  const backgroundColor = resolveHeaderColor(props) || DEFAULT_FALLBACK_COLOR;
  const labelColor = getContrastTextColor(backgroundColor);

  return (
    <SwimlaneColumnHeader
      {...props}
      backgroundColor={backgroundColor}
      labelColor={labelColor}
      isViewCardOptionDisabled={true}
      isReOrderingDisabled={true}
      onOpenCard={() => {}}
      hideHeader={() => {}}
      openCardLabel=""
    >
      {renderReadOnlyHeaderLabel(props, ideaManagerEnabled)}
    </SwimlaneColumnHeader>
  );
}

function ReadOnlyRowHeaderBase(props: IHeader & { isGroupHeader?: boolean }) {
  const ideaManagerEnabled = useFlag('ideaManagerEnabled');
  const backgroundColor = resolveHeaderColor(props) || DEFAULT_FALLBACK_COLOR;
  const labelColor = getContrastTextColor(backgroundColor);

  return (
    <SwimlaneRowHeader
      {...props}
      backgroundColor={backgroundColor}
      labelColor={labelColor}
      isViewCardOptionDisabled={true}
      isReOrderingDisabled={true}
      onOpenCard={() => {}}
      hideHeader={() => {}}
      openCardLabel=""
    >
      {renderReadOnlyHeaderLabel(props, ideaManagerEnabled)}
    </SwimlaneRowHeader>
  );
}

export function ReadOnlyRowHeader(props: IHeader) {
  return <ReadOnlyRowHeaderBase {...props} />;
}

export function ReadOnlyGroupRowHeader(props: IHeader) {
  return <ReadOnlyRowHeaderBase {...props} isGroupHeader={true} />;
}

export const NO_OP_ACTIONS: Readonly<ISwimlaneActions> = {
  headerActions: {
    addHiddenHeaderValue: (_level: 1 | 2, _event: MouseEvent<HTMLButtonElement>) => {},
    setRowHeaderWidth: (_width: number) => {},
  },
  itemCardActions: {
    addLinkedItemHandler: (_itemID: string) => {},
    addSubItemHandler: (_itemID: string) => {},
    copyItemURLHandler: (_itemID: string) => {},
    deleteItemHandler: (_itemID: string) => {},
    duplicateItemHandler: (_itemID: string) => {},
    renameItemHandler: (_itemID: string) => {},
    transferItemHandler: (_itemID: string) => {},
  },
  itemButtonActions: {
    attachmentsHandler: (_itemID: string) => {},
    commentsHandler: (_itemID: string) => {},
    keyDatesHandler: (_itemID: string) => {},
    linkedItemsHandler: (_itemID: string) => {},
  },
} as const;
